/**
 * کارتِ «چند عکسی» در سربرگِ «عکس‌ها» (WHOSUP-244).
 *
 * ═══ داده از کجا می‌آید ═══
 *
 * از WHOSUP-240 (مخزنِ کرالر) به بعد، کرالر علاوه بر `events.image_url`
 * عکس‌های دیگرِ همان رویداد را هم منتشر می‌کند — در `events.metadata.images`:
 *
 *   [{ url, source, page, selected }, …]   عضوِ اول = image_url، سقف ۵
 *
 * رویدادِ تک‌عکسی کلید را اصلاً ندارد. اپ و وب هنوز فقط `image_url` را
 * نشان می‌دهند؛ این کارت برای دیدنِ این است که فهرست‌ها چه شکلی‌اند.
 *
 * RPC جداست (`admin_event_multi_photos`، مایگریشن 0150) و query هم جدا، تا
 * اگر مایگریشن روی دیتابیسی نبود فقط همین کارت خطا بدهد، نه کلِ سربرگ.
 *
 * ═══ «این‌ها یکی هستند» (مایگریشن 0151) ═══
 *
 * گوشه‌ی هر بندانگشتی یک تیک است؛ یک دکمه برای همه‌ی نمونه‌ها ثبت می‌کند،
 * ولی هر گروه فقط درونِ رویدادِ خودش ساخته می‌شود. ثبت‌شده‌ها روی هم،
 * آبشاری، نشان داده می‌شوند. دسته‌بندی و ساختنِ ورودی در
 * `core/lib/multiPhotos.ts` است.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminRepo } from '@/core/data/adminRepo';
import { opsRepo, type EventMultiPhotoImage, type EventMultiPhotoSample } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { clusterPhotos, photoKey, samePhotosPayload } from '@/core/lib/multiPhotos';
import { describeError, reportError } from '@/core/lib/report';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';

const WHERE_HELP =
  'جدولِ events، ستونِ metadata، کلیدِ images — آرایه‌ای از {url, source, page, selected}. '
  + 'عضوِ اول همان ستونِ image_url است و selected دارد، بقیه به ترتیبِ اطمینان، حداکثر ۵. '
  + 'رویدادِ تک‌عکسی این کلید را ندارد. کرالر (WHOSUP-240) می‌نویسدش؛ اپ و وب هنوز فقط image_url را نشان می‌دهند.';

/** یک بندانگشتی، لینک‌شده به خودِ عکس. */
function Thumb({ img }: { img: EventMultiPhotoImage }) {
  const [bad, setBad] = React.useState(false);
  const border = img.invalid ? 'var(--danger)' : img.selected ? 'var(--brand)' : 'var(--line)';
  const title = [
    img.selected ? 'عکسِ اصلی (image_url)' : null,
    img.invalid ? '«غیر معتبر» علامت خورده' : null,
    img.url,
  ].filter(Boolean).join(' · ');
  return (
    <a href={img.url} target="_blank" rel="noreferrer" title={title} style={{ display: 'block' }}>
      {bad ? (
        <span className="err" style={{ display: 'inline-block', width: 72, height: 50, lineHeight: '50px',
          textAlign: 'center', background: 'var(--surface)', border: `2px solid ${border}`, borderRadius: 4 }}
          title="از این‌جا هم لود نشد">✕</span>
      ) : (
        <img src={img.url} alt="" loading="lazy" onError={() => setBad(true)}
          style={{ display: 'block', width: 72, height: 50, objectFit: 'cover', borderRadius: 4,
            border: `2px solid ${border}`, opacity: img.invalid ? 0.5 : 1 }} />
      )}
    </a>
  );
}

/** پله‌ی هر لایه‌ی آبشار، به پیکسل. */
const STEP = 7;

/**
 * یک دسته از فهرست: عکسِ تنها، یا چند عکسی که «یکی هستند» — روی هم، هر
 * کدام کمی پایین‌تر و کنارتر، مثلِ آبشار. تیکِ گوشه کلِ دسته را برمی‌دارد.
 */
function PhotoCluster({ imgs, checked, onToggle, onSplit, busy }: {
  imgs: EventMultiPhotoImage[];
  checked: boolean;
  onToggle: () => void;
  onSplit: () => void;
  busy: boolean;
}) {
  const extra = (imgs.length - 1) * STEP;
  const head = imgs[0];
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      width: 76 + extra }}>
      <div style={{ position: 'relative', width: 72 + 4 + extra, height: 50 + 4 + extra }}>
        {imgs.map((img, i) => (
          <div key={img.url}
            style={{ position: 'absolute', insetInlineStart: i * STEP, top: i * STEP, zIndex: imgs.length - i }}>
            <Thumb img={img} />
          </div>
        ))}
        <input type="checkbox" checked={checked} disabled={busy} onChange={onToggle}
          title={imgs.length > 1 ? 'تیکِ همه‌ی این دسته' : 'انتخاب برای «این‌ها یکی هستند»'}
          style={{ position: 'absolute', top: 2, insetInlineEnd: 2 + extra, zIndex: imgs.length + 1, margin: 0,
            background: 'var(--surface)' }} />
        {imgs.length > 1 ? (
          <span className="chip" title={imgs.map((x) => x.url).join('\n')}
            style={{ position: 'absolute', bottom: 0, insetInlineEnd: 0, zIndex: imgs.length + 1,
              fontSize: 10, padding: '0 4px' }}>
            ×{fmt(imgs.length)}
          </span>
        ) : null}
      </div>
      {head.page ? (
        <a className="mono ltr dim" href={head.page} target="_blank" rel="noreferrer" title={head.page}
          style={{ fontSize: 11, maxWidth: 76, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {head.source ?? 'صفحه'}
        </a>
      ) : (
        <span className="mono ltr dim" style={{ fontSize: 11 }}>{head.source ?? '—'}</span>
      )}
      {imgs.length > 1 ? (
        <button type="button" className="linkbtn" disabled={busy} onClick={onSplit}
          title="این‌ها یکی نیستند — گروه باز می‌شود" style={{ fontSize: 11 }}>
          جدا کن
        </button>
      ) : null}
    </div>
  );
}

export function MultiPhotos() {
  const q = useQuery({ queryKey: ['eventPhotos', 'multi'], queryFn: () => opsRepo.eventMultiPhotos() });
  const qc = useQueryClient();
  // برای هر رویداد، کلیدِ دسته‌های تیک‌خورده.
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const m = useMutation({
    mutationFn: (a: { groups: { event_id: string; urls: string[] }[]; same: boolean }) =>
      adminRepo.setSamePhotos(a.groups, a.same),
    onSuccess: (_r, a) => {
      if (a.same) setPicked({});
      return qc.invalidateQueries({ queryKey: ['eventPhotos', 'multi'] });
    },
    onError: (e) => reportError('admin.photos.same', e),
  });

  const toggle = (eventId: string, key: string) => setPicked((prev) => {
    const cur = prev[eventId] ?? [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    return { ...prev, [eventId]: next };
  });

  const head = (
    <h2><Hint text="چند عکسی" help={WHERE_HELP} /></h2>
  );

  if (q.isLoading) return <div className="card">{head}<p className="dim">…</p></div>;
  if (q.error) {
    return <div className="card">{head}<p className="err">{describeError(q.error)}</p></div>;
  }

  const d = q.data!;
  const t = d.totals;

  if (t.with_key === 0) {
    return (
      <div className="card">
        {head}
        <p className="dim" style={{ marginTop: 10 }}>
          هیچ رویدادِ زنده‌ای هنوز فهرستِ عکس ندارد. کرالر از WHOSUP-240 به بعد
          عکس‌های اضافه را در <span className="mono ltr">events.metadata.images</span> می‌نویسد؛
          اگر این‌جا خالی است، یعنی شبانه‌ی کرالر با آن نسخه هنوز روی این دیتابیس ننشسته.
        </p>
      </div>
    );
  }

  const share = t.with_photo > 0 ? `${Math.round((t.with_list / t.with_photo) * 100)}٪` : '—';
  // دو شمارشِ سلامت: هر فهرست باید با عکسِ اصلی شروع شود و همان selected باشد.
  const offMain = t.with_list - t.first_is_main;
  const offSelected = t.with_list - t.first_selected;
  const payload = samePhotosPayload(d.samples, picked);

  return (
    <div className="card">
      {head}

      <div className="grid n4" style={{ marginTop: 10 }}>
        <div className="stat">
          <div className="n">{fmt(t.with_list)}</div>
          <div className="k">
            <Hint text="رویدادِ چند عکسی"
              help={`${share} از ${fmt(t.with_photo)} رویدادِ زنده‌ی عکس‌دار، دو عکس یا بیشتر دارند.`} />
          </div>
        </div>
        <div className="stat">
          <div className="n">{fmt(t.images - t.with_list)}</div>
          <div className="k">
            <Hint text="عکسِ اضافه"
              help={`روی‌هم ${fmt(t.images)} عکس در فهرست‌ها؛ منهای یک عکسِ اصلیِ هر رویداد.`} />
          </div>
        </div>
        <div className="stat">
          <div className="n">{fmt(t.capped)}</div>
          <div className="k"><Hint text="به سقفِ ۵ خورده" help="بیشتر از ۵ را کرالر می‌بُرد." /></div>
        </div>
        <div className="stat">
          <div className={`n${t.invalid_in_list > 0 ? ' err' : ''}`}>{fmt(t.invalid_in_list)}</div>
          <div className="k">
            <Hint text="غیرمعتبر در فهرست"
              help="عکس‌هایی از فهرست که «غیر معتبر» علامت خورده‌اند. پرچمِ image_invalid فقط عکسِ اصلی را پنهان می‌کند؛ وقتی اپ فهرست را نشان دهد باید این‌ها را خودش کنار بگذارد." />
          </div>
        </div>
      </div>

      <p className="dim" style={{ marginTop: 8 }}>
        اندازه‌ی فهرست‌ها:{' '}
        {d.by_size.map((s) => (
          <span key={s.n} className="chip" style={{ marginInlineEnd: 6 }}>
            {fmt(s.n)} عکس · {fmt(s.events)} رویداد
          </span>
        ))}
      </p>

      {offMain > 0 || offSelected > 0 || t.with_key > t.with_list ? (
        <ul className="err" style={{ marginTop: 6 }}>
          {offMain > 0 ? <li>{fmt(offMain)} فهرست با عکسِ اصلیِ رویداد (image_url) شروع نمی‌شود.</li> : null}
          {offSelected > 0 ? <li>عضوِ اولِ {fmt(offSelected)} فهرست علامتِ selected ندارد.</li> : null}
          {t.with_key > t.with_list ? (
            <li>{fmt(t.with_key - t.with_list)} رویداد کلیدِ images دارند ولی کمتر از دو عکسِ سالم — طبقِ قرارداد نباید باشد.</li>
          ) : null}
        </ul>
      ) : null}

      <h3 style={{ marginTop: 14 }}>به تفکیکِ منبع</h3>
      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>منبع</th><th>رویداد</th><th>چند عکسی</th>
              <th><Hint text="عکس" help="جمعِ اندازه‌ی فهرست‌ها، با عکسِ اصلی." /></th>
              <th>بیشترین</th>
            </tr>
          </thead>
          <tbody>
            {d.by_source.map((s) => (
              <tr key={s.source}>
                <td className="mono ltr">{s.source}</td>
                <td className="num">{fmt(s.events)}</td>
                <td className="num">{fmt(s.with_list)}</td>
                <td className="num">{fmt(s.images)}</td>
                <td className="num">{fmt(s.max_n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 14 }}>
        <Hint text="نمونه‌ها"
          help="پرعکس‌ترها اول، حداکثر ۴۰. کادرِ رنگی = عکسِ اصلی، قرمز = غیر معتبر. کلیک روی عکس خودِ عکس را باز می‌کند و اسمِ زیرش صفحه‌ای را که عکس از آن آمده. عکس‌هایی که «یکی هستند» ثبت شده‌اند روی هم، آبشاری، نشان داده می‌شوند." />
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0' }}>
        <button type="button" className="btn" disabled={payload.groups.length === 0 || m.isPending}
          onClick={() => m.mutate({ groups: payload.groups, same: true })}
          title="هر گروه فقط درونِ رویدادِ خودش ساخته می‌شود، نه بین رویدادها.">
          این‌ها یکی هستند
          {payload.groups.length > 0 ? ` (${fmt(payload.groups.length)} رویداد)` : ''}
        </button>
        <span className="dim">
          {payload.groups.length === 0 && payload.lonely === 0
            ? 'گوشه‌ی بالای عکس‌هایی را که یکی‌اند تیک بزن — در هر رویداد دست‌کم دوتا.'
            : null}
          {payload.lonely > 0
            ? `${fmt(payload.lonely)} رویداد فقط یک تیک دارد و حساب نمی‌شود.`
            : null}
          {t.same_marked > 0 ? ` تا حالا روی ${fmt(t.same_marked)} رویداد ثبت شده.` : null}
        </span>
        {m.error ? <span className="err">{describeError(m.error)}</span> : null}
      </div>
      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr><th>رویداد</th><th>منبع</th><th>تاریخ</th><th>عکس‌ها</th></tr>
          </thead>
          <tbody>
            {d.samples.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.url
                    ? <a href={s.url} target="_blank" rel="noreferrer" title={s.url}>{s.title}</a>
                    : s.title}
                </td>
                <td className="mono ltr">{s.source}</td>
                <td className="num">{s.starts_at ? formatDateTime(s.starts_at) : <span className="dim">—</span>}</td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-start' }}>
                    {clusterPhotos(s.images).map((c) => {
                      const key = photoKey(c[0]);
                      return (
                        <PhotoCluster key={key} imgs={c} busy={m.isPending}
                          checked={(picked[s.id] ?? []).includes(key)}
                          onToggle={() => toggle(s.id, key)}
                          onSplit={() => m.mutate({
                            groups: [{ event_id: s.id, urls: c.map((x) => x.url) }], same: false })} />
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
